import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { usePack } from '../../contexts/PackContext';
import Icon from '../AppIcon';
import Button from './Button';
import { useAlerts } from '../../jobs/schedulers';

const Sidebar = ({ isCollapsed = false, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { activePack } = usePack();
  
  // Derived pack booleans for backward compatibility  
  const isDentist = activePack === 'dentist';
  const isCreche = activePack === 'creche';
  const isMSP = activePack === 'msp';
  const isCapital = activePack === 'capital';
  
  // Alert integration
  const { alerts, alertCount, resolveAlert } = useAlerts();

  // Define the sidebar menu structure based on pack type
  const getMenuStructure = () => {
    if (isCapital) {
      return [
        // ===== CAPITAL PACK - PORTFOLIO INTELLIGENCE PLATFORM =====
        {
          id: 'portfolio-dashboard',
          label: 'Portfolio Overview',
          icon: 'Building2',
          path: '/capital-portfolio-dashboard',
          type: 'single',
          alertTypes: ['portfolio_alert', 'performance_alert', 'risk_alert']
        },
        {
          id: 'portfolio-companies',
          label: 'Portfolio Companies',
          icon: 'Briefcase',
          type: 'group',
          alertTypes: ['company_alert', 'valuation_alert', 'covenant_breach'],
          items: [
            { label: 'Company Performance', path: '/portfolio-analytics-overview', icon: 'TrendingUp', alertTypes: ['performance_alert'] },
            { label: 'Investment Analysis', path: '/valuation-analysis', icon: 'Calculator', alertTypes: ['valuation_alert'] },
            { label: 'Financial Health', path: '/capital-company-detail', icon: 'Heart', alertTypes: ['covenant_breach', 'cash_flow_warning'] }
          ]
        },
        {
          id: 'intelligence-analytics',
          label: 'Intelligence & Analytics', 
          icon: 'Brain',
          type: 'group',
          alertTypes: ['ai_insight', 'market_intelligence', 'risk_prediction'],
          items: [
            { label: 'Portfolio Copilot', path: '/capital-portfolio-copilot', icon: 'MessageSquare', alertTypes: ['ai_insight'] },
            { label: 'Predictive Analytics', path: '/capital-predictive-covenant-monitor', icon: 'TrendingUp', alertTypes: ['risk_prediction'] },
            { label: 'Market Intelligence', path: '/market-intelligence', icon: 'Globe', alertTypes: ['market_intelligence'] },
            { label: 'Cross-Sector Analysis', path: '/correlation-analysis', icon: 'BarChart3', alertTypes: ['sector_alert'] }
          ]
        },
        {
          id: 'financial-monitoring',
          label: 'Financial Monitoring',
          icon: 'PoundSterling',
          type: 'group',
          alertTypes: ['covenant_breach', 'cash_flow_warning', 'runway_alert'],
          items: [
            { label: 'Cash Flow Tracking', path: '/cash-flow-forecasting', icon: 'Activity', alertTypes: ['cash_flow_warning', 'runway_alert'] },
            { label: 'Risk Dashboard', path: '/capital-early-warning-centre', icon: 'AlertTriangle', alertTypes: ['risk_alert'] }
          ]
        },
        {
          id: 'scenario-planning',
          label: 'Scenario Planning',
          icon: 'Calculator',
          type: 'group',
          alertTypes: ['scenario_alert', 'forecast_warning'],
          items: [
            { label: 'Scenario Simulator', path: '/capital-scenario-simulator', icon: 'Sliders', alertTypes: ['scenario_alert'] },
            { label: 'Exit Planning', path: '/what-if-scenario-planning', icon: 'LogOut', alertTypes: ['exit_opportunity'] },
            { label: 'Market Modeling', path: '/market-penetration-analysis', icon: 'LineChart', alertTypes: ['forecast_warning'] }
          ]
        },
        {
          id: 'reporting-communication',
          label: 'Capital Reporting',
          icon: 'FileText',
          type: 'group',
          alertTypes: ['report_ready', 'lp_communication'],
          items: [
            { label: 'LP Pack Generator', path: '/capital-lp-pack-auto-writer', icon: 'Package', alertTypes: ['report_ready'] },
            { label: 'Executive Reports', path: '/executive-dashboard-builder', icon: 'FileText', alertTypes: ['report_ready'] },
            { label: 'Board Materials', path: '/reports', icon: 'Users', alertTypes: ['board_prep'] }
          ]
        },
        {
          id: 'integrations-data',
          label: 'Integrations & Data',
          icon: 'Database',
          type: 'group',
          alertTypes: ['sync_error', 'data_quality'],
          items: [
            { label: 'Data Connectors', path: '/capital-integrations-management', icon: 'Plug', alertTypes: ['sync_error'] },
            { label: 'Portfolio Sync', path: '/data-synchronization-manager', icon: 'RefreshCw', alertTypes: ['sync_error'] },
            { label: 'Data Quality', path: '/data-intelligence-suite-hub', icon: 'CheckCircle', alertTypes: ['data_quality'] },
            { label: 'Settings', path: '/capital-settings-configuration', icon: 'Settings', alertTypes: ['config_required'] }
          ]
        }
      ];
    } else if (isCreche) {
      return [
        // ===== FREQUENT USE - Daily Workflow =====
        {
          id: 'dashboard',
          label: 'Today',
          icon: 'Calendar',
          path: '/creche/today',
          type: 'single',
          alertTypes: ['ratio_breach', 'staff_shortage'] // Alert types for Today dashboard
        },
        {
          id: 'operations',
          label: 'Operations',
          icon: 'Activity',
          type: 'group',
          alertTypes: ['ratio_breach'], // Operations alerts
          items: [
            { label: 'Operations Hub', path: '/cross-centre-operations-hub', icon: 'Network', alertTypes: ['cross_centre_alert'] },
            { label: 'Staff Mobility', path: '/staff-mobility-management', icon: 'Users', alertTypes: ['mobility_request'] },
            { label: 'Attendance', path: '/creche/attendance', icon: 'UserCheck' },
            { label: 'Group Management', path: '/creche/group', icon: 'Users' },
            { label: 'Staff Rota', path: '/creche/rota', icon: 'Calendar', alertTypes: ['ratio_breach', 'staff_shortage'] }
          ]
        },
        {
          id: 'management',
          label: 'Management',
          icon: 'Settings',
          type: 'group',
          alertTypes: ['funding_insufficiency', 'certificate_expiry'], // Management alerts
          items: [
            { label: 'Funding', path: '/creche/funding', icon: 'PoundSterling', alertTypes: ['funding_insufficiency'] },
            { label: 'Billing', path: '/creche/billing', icon: 'CreditCard' },
            { label: 'Compliance', path: '/creche/compliance', icon: 'Shield', alertTypes: ['certificate_expiry', 'compliance_violation'] }
          ]
        },
        // ===== REGULAR TASKS - Centre & Community Management =====
        {
          id: 'centres',
          label: 'Centres',
          icon: 'Building2',
          type: 'group',
          alertTypes: ['capacity_optimization', 'compliance_violation'],
          items: [
            { label: 'Overview', path: '/centres-overview', icon: 'LayoutGrid', alertTypes: ['capacity_optimization'] },
            { label: 'Onboarding', path: '/centre-onboarding-setup-wizard', icon: 'UserPlus', alertTypes: ['setup_incomplete'] },
            { label: 'Reputation', path: '/cr-che-reputation-management', icon: 'Star', alertTypes: ['reputation_alert'] },
            { label: 'Analytics', path: '/cr-che-analytics-hub', icon: 'BarChart3', alertTypes: ['performance_insight'] },
            { label: 'Predictive', path: '/predictive-analytics-forecasting', icon: 'TrendingUp', alertTypes: ['forecast_alert', 'churn_risk'] },
          ]
        },
        // ===== HIDDEN SECTIONS FOR MANAGEMENT COMPANY VIEW =====
        // Community section - hidden for executive focus
        /*
        {
          id: 'community',
          label: 'Community',
          icon: 'Users',
          type: 'group',
          alertTypes: ['parent_feedback', 'community_event', 'engagement_alert', 'transition_alert'],
          items: [
            { label: 'Parent Portal', path: '/parent-portal-hub', icon: 'Baby', alertTypes: ['parent_feedback', 'communication_urgent'] },
            { label: 'Community Hub', path: '/community-engagement-hub', icon: 'Heart', alertTypes: ['community_event', 'volunteer_needed', 'partnership_opportunity'] },
            { label: 'School Transitions', path: '/school-transition-planning', icon: 'GraduationCap', alertTypes: ['transition_alert', 'readiness_concern', 'school_application'] }
          ]
        },
        */
        // ===== PLANNING TOOLS - Strategic & Operational Planning =====
        {
          id: 'strategic-planning',
          label: 'Strategic Planning',
          icon: 'Target',
          type: 'group',
          alertTypes: ['expansion_opportunity', 'market_risk', 'valuation_alert'],
          items: [
            { label: 'Planning Hub', path: '/strategic-planning-hub', icon: 'Command', alertTypes: ['strategic_milestone'] },
            { label: 'Site Selection', path: '/site-selection-feasibility-analysis', icon: 'MapPin', alertTypes: ['expansion_opportunity'] },
            { label: 'Market Analysis', path: '/market-penetration-analysis', icon: 'TrendingUp', alertTypes: ['market_risk'] },
            { label: 'Acquisitions', path: '/acquisition-target-screening', icon: 'Building2', alertTypes: ['acquisition_opportunity'] },
            { label: 'ROI Modeling', path: '/expansion-roi-modeling', icon: 'Calculator', alertTypes: ['valuation_alert'] }
          ]
        },
        // OpsPlus section - hidden for executive focus
        /*
        {
          id: 'opsplus',
          label: 'OpsPlus',
          icon: 'TrendingUp',
          type: 'group',
          alertTypes: ['capacity_optimization', 'cost_variance'],
          items: [
            { label: 'Room Planner', path: '/room-planner', icon: 'Layout', alertTypes: ['capacity_optimization'] },
            { label: 'Staffing Planner', path: '/staffing-planner', icon: 'Users', alertTypes: ['cost_variance', 'staff_shortage'] }
          ]
        },
        */
        {
          id: 'financeplus',
          label: 'FinancePlus',
          icon: 'PoundSterling',
          type: 'group',
          alertTypes: ['debt_covenant_breach', 'cash_flow_warning', 'valuation_change'],
          items: [
            { label: 'Finance Dashboard', path: '/finance-dashboard', icon: 'TrendingUp', alertTypes: ['debt_covenant_breach', 'cash_flow_warning'] },
            { label: 'Valuation Analysis', path: '/valuation-analysis', icon: 'Calculator', alertTypes: ['valuation_change', 'market_update'] },
            { label: 'Cash Flow Forecasting', path: '/cash-flow-forecasting', icon: 'Activity', alertTypes: ['cash_flow_warning', 'liquidity_risk'] }
          ]
        },
        // Financial Optimization section - hidden for executive focus
        /*
        {
          id: 'financial-optimization',
          label: 'Financial Optimization',
          icon: 'PoundSterling',
          type: 'group',
          alertTypes: ['financial_alert', 'revenue_opportunity', 'cost_optimization'],
          items: [
            { label: 'Optimization Hub', path: '/financial-optimization-hub', icon: 'TrendingUp', alertTypes: ['financial_alert', 'budget_variance'] },
            { label: 'Revenue Management', path: '/revenue-management-tools', icon: 'DollarSign', alertTypes: ['revenue_opportunity', 'pricing_optimization'] },
            { label: 'Dynamic Pricing', path: '/dynamic-pricing-engine', icon: 'Zap', alertTypes: ['pricing_alert', 'demand_optimization'] }
          ]
        },
        */
        {
          id: 'marketplace',
          label: 'Marketplace',
          icon: 'Store',
          type: 'group',
          alertTypes: ['vendor_alert', 'procurement_opportunity', 'cost_savings'],
          items: [
            { label: 'Marketplace Hub', path: '/resource-marketplace-hub', icon: 'ShoppingBag', alertTypes: ['marketplace_alert'] },
            { label: 'Vendor Management', path: '/vendor-management-portal', icon: 'Users', alertTypes: ['vendor_alert', 'contract_renewal'] },
            { label: 'Procurement', path: '/procurement-marketplace', icon: 'ShoppingCart', alertTypes: ['procurement_opportunity'] },
            { label: 'Service Providers', path: '/service-provider-directory', icon: 'Briefcase', alertTypes: ['service_alert'] },
            { label: 'Resource Sharing', path: '/resource-sharing-network', icon: 'Share2', alertTypes: ['sharing_request'] },
            { label: 'Equipment Trading', path: '/equipment-marketplace', icon: 'Package', alertTypes: ['equipment_alert'] }
          ]
        },
        // Supply Chain section - hidden for executive focus
        /*
        {
          id: 'supply-chain',
          label: 'Supply Chain',
          icon: 'Truck',
          type: 'group',
          alertTypes: ['inventory_alert', 'supplier_issue', 'quality_concern'],
          items: [
            { label: 'Inventory Management', path: '/inventory-management', icon: 'Package2', alertTypes: ['inventory_alert', 'stock_low'] },
            { label: 'Auto Reordering', path: '/automated-reordering', icon: 'RefreshCw', alertTypes: ['reorder_trigger'] },
            { label: 'Supplier Performance', path: '/supplier-performance-tracking', icon: 'BarChart3', alertTypes: ['supplier_issue'] },
            { label: 'Cost Comparison', path: '/cost-comparison-engine', icon: 'Calculator', alertTypes: ['price_alert'] },
            { label: 'Contract Management', path: '/contract-management', icon: 'FileText', alertTypes: ['contract_expiry'] },
            { label: 'Quality Control', path: '/quality-control-systems', icon: 'Shield', alertTypes: ['quality_concern'] }
          ]
        },
        */
        // ===== TECHNICAL/REPORTING - Advanced Features & Integration =====
        {
          id: 'data-intelligence',
          label: 'Data Intelligence',
          icon: 'Brain',
          type: 'group',
          alertTypes: ['data_quality_alert', 'ai_recommendation', 'anomaly_detection'],
          items: [
            { label: 'Intelligence Hub', path: '/data-intelligence-suite-hub', icon: 'Database', alertTypes: ['data_quality_alert'] },
            { label: 'AI Insights', path: '/ai-insights-dashboard', icon: 'Brain', alertTypes: ['ai_recommendation', 'predictive_alert'] },
            { label: 'Data Orchestrator', path: '/smart-data-orchestrator', icon: 'Zap', alertTypes: ['workflow_failure', 'data_sync_error'] }
          ]
        },
        {
          id: 'advanced-reporting',
          label: 'Advanced Reporting',
          icon: 'BarChart3',
          type: 'group',
          alertTypes: ['report_alert', 'dashboard_update', 'scenario_insight'],
          items: [
            { label: 'Dashboard Builder', path: '/executive-dashboard-builder', icon: 'Layout', alertTypes: ['dashboard_update'] },
            { label: 'Scenario Planning', path: '/what-if-scenario-planning', icon: 'Calculator', alertTypes: ['scenario_insight', 'forecast_alert'] },
            { label: 'Competitive Intelligence', path: '/competitive-intelligence-dashboard', icon: 'Target', alertTypes: ['competitive_alert', 'market_intelligence'] }
          ]
        },
        {
          id: 'system-integration',
          label: 'System Integration',
          icon: 'Workflow',
          type: 'group',
          alertTypes: ['integration_failure', 'sync_error', 'api_alert', 'system_connectivity'],
          items: [
            { label: 'Integration Wizard', path: '/integration-setup-wizard', icon: 'Settings', alertTypes: ['setup_incomplete', 'configuration_error'] },
            { label: 'API Management', path: '/api-management-console', icon: 'Plug', alertTypes: ['api_alert', 'connection_failure'] },
            { label: 'Data Sync Manager', path: '/data-synchronization-manager', icon: 'RefreshCw', alertTypes: ['sync_error', 'data_conflict'] }
          ]
        }
      ];
    } else if (isDentist) {
      return [
        {
          id: 'dashboard',
          label: 'Dashboard',
          icon: 'Home',
          path: '/home-dashboard',
          type: 'single'
        },
        {
          id: 'operations',
          label: 'Operations',
          icon: 'Activity',
          type: 'group',
          items: [
            { label: 'Practices', path: '/practices-management', icon: 'Stethoscope' },
            { label: 'AI Insights', path: '/ai-insights', icon: 'Brain' },
            { label: 'Financials', path: '/financials-dashboard', icon: 'TrendingUp' }
          ]
        },
        {
          id: 'reputation',
          label: 'Reputation',
          icon: 'Shield',
          type: 'group',
          items: [
            { label: 'Network Overview', path: '/network', icon: 'Network' },
            { label: 'Locations', path: '/reputation-locations', icon: 'MapPin' },
            { label: 'Health Audit', path: '/reputation-health-audit', icon: 'Heart' },
            { label: 'Settings', path: '/reputation-settings', icon: 'Settings' }
          ]
        },
        {
          id: 'integrations',
          label: 'Integrations',
          icon: 'Plug',
          type: 'group',
          items: [
            { label: 'Management', path: '/integrations-management', icon: 'Settings2' },
            { label: 'Data Hub', path: '/data-hub/profiles', icon: 'Database' },
            { label: 'Smart Orchestrator', path: '/smart-orchestrator', icon: 'Zap' },
            { label: 'Entity Resolution', path: '/entity-resolution-queue', icon: 'GitMerge' },
            { label: 'Payer & Fee Schedules', path: '/payer-fee-schedules', icon: 'CreditCard' }
          ]
        },
        {
          id: 'business-tools',
          label: 'Business Tools',
          icon: 'Briefcase',
          type: 'group',
          items: [
            { label: 'Playbooks', path: '/playbooks-management', icon: 'BookOpen' },
            { label: 'Supplies & Vendors', path: '/supplies-vendors', icon: 'Package2' },
            { label: 'Reports', path: '/reports', icon: 'FileText' }
          ]
        },
        {
          id: 'administration',
          label: 'Administration',
          icon: 'Settings',
          type: 'group',
          items: [
            { label: 'Admin Panel', path: '/admin-panel', icon: 'Shield' }
          ]
        }
      ];
    } else {
      // MSP pack structure
      return [
        {
          id: 'dashboard',
          label: 'Dashboard',
          icon: 'Home',
          path: '/msp-sector-performance', // FIXED: MSP has dedicated dashboard
          type: 'single'
        },
        {
          id: 'portfolio',
          label: 'Portfolio',
          icon: 'Building2',
          type: 'group',
          items: [
            { label: 'Companies', path: '/companies', icon: 'Building' },
            { label: 'AI Insights', path: '/ai-insights', icon: 'Brain' },
            { label: 'Financials', path: '/financials-dashboard', icon: 'TrendingUp' }
          ]
        },
        {
          id: 'reputation',
          label: 'Reputation',
          icon: 'Shield',
          type: 'group',
          items: [
            { label: 'Network Overview', path: '/network', icon: 'Network' },
            { label: 'Locations', path: '/reputation-locations', icon: 'MapPin' },
            { label: 'Health Audit', path: '/reputation-health-audit', icon: 'Heart' },
            { label: 'Settings', path: '/reputation-settings', icon: 'Settings' }
          ]
        },
        {
          id: 'integrations',
          label: 'Integrations',
          icon: 'Plug',
          type: 'group',
          items: [
            { label: 'Management', path: '/integrations-management', icon: 'Settings2' },
            { label: 'Data Hub', path: '/data-hub/profiles', icon: 'Database' },
            { label: 'Smart Orchestrator', path: '/smart-orchestrator', icon: 'Zap' },
            { label: 'Entity Resolution', path: '/entity-resolution-queue', icon: 'GitMerge' }
          ]
        },
        {
          id: 'business-tools',
          label: 'Business Tools',
          icon: 'Briefcase',
          type: 'group',
          items: [
            { label: 'Playbooks', path: '/playbooks-management', icon: 'BookOpen' },
            { label: 'Marketplace', path: '/marketplace', icon: 'Store' },
            { label: 'Reports', path: '/reports', icon: 'FileText' }
          ]
        },
        {
          id: 'msp-management',
          label: 'MSP Management',
          icon: 'Building',
          type: 'group',
          items: [
            { label: 'Rebates Management', path: '/rebates-management', icon: 'DollarSign' },
            { label: 'Licenses Management', path: '/licenses-management', icon: 'Key' },
            { label: 'Cloud Cost Management', path: '/cloud-cost-management', icon: 'Cloud' }
          ]
        },
        {
          id: 'administration',
          label: 'Administration',
          icon: 'Settings',
          type: 'group',
          items: [
            { label: 'Admin Panel', path: '/admin-panel', icon: 'Shield' }
          ]
        }
      ];
    }
  };
  
  // Find which group contains the current path
  const getCurrentActiveGroup = () => {
    const menuStructure = getMenuStructure();
    for (const section of menuStructure) {
      if (section?.type === 'group' && section?.items) {
        const isActive = section?.items?.some(item => {
          // Handle exact path matches
          if (location?.pathname === item?.path) return true;
          
          // Handle special path patterns
          if (item?.path === '/data-hub/profiles' && location?.pathname?.startsWith('/data-hub')) return true;
          
          // Handle creche sub-paths - removed the broad /creche matching that was interfering
          if (item?.path?.startsWith('/creche/') && location?.pathname === item?.path) return true;
          
          // Handle capital pack routes that should expand navigation
          if (item?.path?.startsWith('/capital-') && location?.pathname === item?.path) return true;
          
          // Handle other portfolio routes that should keep navigation open
          if ((item?.path === '/valuation-analysis' || item?.path === '/portfolio-analytics-overview' || 
               item?.path === '/executive-dashboard-builder' || item?.path === '/market-intelligence' ||
               item?.path === '/data-intelligence-suite-hub') && location?.pathname === item?.path) return true;
          
          return false;
        });
        if (isActive) return section?.id;
      } else if (section?.type === 'single') {
        // Handle single menu items
        if (location?.pathname === section?.path) return section?.id;
        
        // Special case for creche/today dashboard
        if (section?.path === '/creche/today' && location?.pathname === '/creche/today') return section?.id;
      }
    }
    return null;
  };

  // State initialization with proper fallback
  const [expandedGroup, setExpandedGroup] = useState(() => {
    const activeGroup = getCurrentActiveGroup();
    if (activeGroup) return activeGroup;
    return isCreche ? 'operations' : (isDentist ? 'operations' : 'portfolio');
  });
  
  const [isCompactMode, setIsCompactMode] = useState(false);

  // Update expanded group when route changes (smart contextual expansion)
  useEffect(() => {
    const activeGroup = getCurrentActiveGroup();
    if (activeGroup && activeGroup !== expandedGroup && !isCompactMode) {
      setExpandedGroup(activeGroup);
    }
  }, [location?.pathname, isCompactMode]); // Removed expandedGroup from deps to prevent loops

  const handleNavigation = (path) => {
    navigate(path);
  };

  // Simplified accordion toggle behavior
  const toggleGroup = (groupId) => {
    if (isCollapsed) return; // Don't expand groups when sidebar is collapsed
    
    // Accordion behavior: only one section open at a time
    if (expandedGroup === groupId) {
      // If clicking the currently expanded group, collapse it
      setExpandedGroup(null);
      setIsCompactMode(false);
    } else {
      // Expand the clicked group and close others
      setExpandedGroup(groupId);
      setIsCompactMode(false);
    }
  };

  // Toggle compact mode (collapse all sections)
  const toggleCompactMode = () => {
    setIsCompactMode(!isCompactMode);
    if (!isCompactMode) {
      // When entering compact mode, remember current expanded group
      const activeGroup = getCurrentActiveGroup();
      if (activeGroup) {
        setExpandedGroup(activeGroup);
      }
    }
  };

  const isPathActive = (path) => {
    // Handle exact matches first
    if (location?.pathname === path) return true;
    
    // Handle special cases
    if (path === '/home-dashboard') {
      return location?.pathname === '/home-dashboard';
    }
    
    // Handle creche/today specifically (don't match all /creche paths)
    if (path === '/creche/today') {
      return location?.pathname === '/creche/today';
    }
    
    // Handle data-hub paths specially
    if (path === '/data-hub/profiles') {
      return location?.pathname?.startsWith('/data-hub');
    }
    
    return false;
  };

  const isGroupActive = (items) => {
    return items?.some(item => isPathActive(item?.path));
  };

  const isGroupExpanded = (groupId) => {
    if (isCollapsed || isCompactMode) return false;
    return expandedGroup === groupId;
  };

  // ============================================================================
  // ALERT BADGE LOGIC
  // ============================================================================

  // Get alerts for a specific menu section or item
  const getAlertsForSection = (section) => {
    if (!section?.alertTypes || !alerts?.length) return [];
    return alerts?.filter(alert => section?.alertTypes?.includes(alert?.type));
  };

  const getAlertsForItem = (item) => {
    if (!item?.alertTypes || !alerts?.length) return [];
    return alerts?.filter(alert => item?.alertTypes?.includes(alert?.type));
  };

  // Render alert badge
  const AlertBadge = ({ count, severity = 'high', size = 'sm' }) => {
    if (!count || count === 0) return null;

    const sizeClasses = {
      xs: 'h-3 w-3 text-xs',
      sm: 'h-4 w-4 text-xs',
      md: 'h-5 w-5 text-sm'
    };

    const severityColors = {
      low: 'bg-yellow-500',
      medium: 'bg-orange-500', 
      high: 'bg-red-500',
      critical: 'bg-red-600 animate-pulse'
    };

    return (
      <span 
        className={`
          inline-flex items-center justify-center 
          ${sizeClasses?.[size]} 
          ${severityColors?.[severity]}
          text-white font-bold rounded-full 
          shadow-sm border border-white
        `}
        title={`${count} alert${count > 1 ? 's' : ''}`}
      >
        {count > 9 ? '9+' : count}
      </span>
    );
  };

  // Get highest severity for alerts
  const getHighestSeverity = (alertList) => {
    if (!alertList?.length) return 'low';
    const severityOrder = ['low', 'medium', 'high', 'critical'];
    return alertList?.reduce((highest, alert) => {
      const currentIndex = severityOrder?.indexOf(alert?.severity);
      const highestIndex = severityOrder?.indexOf(highest);
      return currentIndex > highestIndex ? alert?.severity : highest;
    }, 'low');
  };

  const menuStructure = getMenuStructure();

  return (
    <>
      {/* Sidebar */}
      <aside className={`fixed left-0 top-16 h-[calc(100vh-4rem)] bg-card border-r border-border transition-all duration-300 z-50 ${
        isCollapsed ? 'w-16' : 'w-64'
      }`}>
        <div className="flex flex-col h-full">
          {/* Sidebar Toggle */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            {!isCollapsed && (
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-muted-foreground">Navigation</span>
                {/* Global alert indicator */}
                {alertCount > 0 && (
                  <AlertBadge 
                    count={alertCount} 
                    severity={getHighestSeverity(alerts)} 
                    size="md"
                  />
                )}
              </div>
            )}
            <div className="flex items-center space-x-2">
              {/* Compact Mode Toggle */}
              {!isCollapsed && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleCompactMode}
                  className="h-8 w-8"
                  title={isCompactMode ? "Expand sections" : "Collapse all"}
                >
                  <Icon name={isCompactMode ? "Expand" : "Minimize2"} size={14} />
                </Button>
              )}
              {/* Sidebar Collapse Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={onToggle}
                className="h-8 w-8"
              >
                <Icon name={isCollapsed ? "ChevronRight" : "ChevronLeft"} size={16} />
                {/* Collapsed sidebar alert indicator */}
                {isCollapsed && alertCount > 0 && (
                  <div className="absolute -top-1 -right-1">
                    <AlertBadge 
                      count={alertCount} 
                      severity={getHighestSeverity(alerts)} 
                      size="xs"
                    />
                  </div>
                )}
              </Button>
            </div>
          </div>

          {/* Menu Items */}
          <nav className="flex-1 overflow-y-auto py-4">
            <div className="space-y-2">
              {menuStructure?.map((section) => {
                const sectionAlerts = getAlertsForSection(section);
                const sectionAlertCount = sectionAlerts?.length;
                
                return (
                  <div key={section?.id}>
                    {section?.type === 'single' ? (
                      // Single menu item (Dashboard/Today)
                      (<button
                        onClick={() => handleNavigation(section?.path)}
                        className={`w-full flex items-center justify-between px-4 py-2 mx-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                          isPathActive(section?.path)
                            ? 'bg-primary text-primary-foreground shadow-sm'
                            : 'text-foreground hover:bg-muted hover:text-foreground'
                        }`}
                        title={isCollapsed ? section?.label : ''}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="relative">
                            <Icon name={section?.icon} size={20} />
                            {/* Alert badge on icon for collapsed sidebar */}
                            {isCollapsed && sectionAlertCount > 0 && (
                              <div className="absolute -top-2 -right-2">
                                <AlertBadge 
                                  count={sectionAlertCount} 
                                  severity={getHighestSeverity(sectionAlerts)}
                                  size="xs"
                                />
                              </div>
                            )}
                          </div>
                          {!isCollapsed && <span>{section?.label}</span>}
                        </div>
                        {/* Alert badge for expanded sidebar */}
                        {!isCollapsed && sectionAlertCount > 0 && (
                          <AlertBadge 
                            count={sectionAlertCount} 
                            severity={getHighestSeverity(sectionAlerts)}
                          />
                        )}
                      </button>)
                    ) : (
                      // Group menu item
                      (<div>
                        <button
                          onClick={() => toggleGroup(section?.id)}
                          className={`w-full flex items-center justify-between px-4 py-2 mx-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                            isGroupActive(section?.items)
                              ? 'bg-primary/10 text-primary' :'text-foreground hover:bg-muted'
                          } ${isCollapsed ? 'justify-center' : ''}`}
                          title={isCollapsed ? section?.label : ''}
                          disabled={isCollapsed}
                        >
                          <div className="flex items-center space-x-3">
                            <div className="relative">
                              <Icon name={section?.icon} size={20} />
                              {/* Alert badge on icon for collapsed sidebar */}
                              {isCollapsed && sectionAlertCount > 0 && (
                                <div className="absolute -top-2 -right-2">
                                  <AlertBadge 
                                    count={sectionAlertCount} 
                                    severity={getHighestSeverity(sectionAlerts)}
                                    size="xs"
                                  />
                                </div>
                              )}
                            </div>
                            {!isCollapsed && <span>{section?.label}</span>}
                          </div>
                          {!isCollapsed && (
                            <div className="flex items-center space-x-2">
                              {/* Alert badge for expanded sidebar */}
                              {sectionAlertCount > 0 && (
                                <AlertBadge 
                                  count={sectionAlertCount} 
                                  severity={getHighestSeverity(sectionAlerts)}
                                />
                              )}
                              {/* Active indicator */}
                              {isGroupActive(section?.items) && (
                                <div className="w-2 h-2 bg-primary rounded-full" />
                              )}
                              <Icon 
                                name={isGroupExpanded(section?.id) ? "ChevronUp" : "ChevronDown"} 
                                size={16}
                                className="transition-transform duration-200" 
                              />
                            </div>
                          )}
                        </button>
                        {/* Submenu Items with proper conditional rendering */}
                        {isGroupExpanded(section?.id) && (
                          <div className="mt-1 space-y-1 animate-in slide-in-from-top-2 duration-200">
                            {section?.items?.map((item) => {
                              const itemAlerts = getAlertsForItem(item);
                              const itemAlertCount = itemAlerts?.length;
                              
                              return (
                                <button
                                  key={item?.path}
                                  onClick={() => handleNavigation(item?.path)}
                                  className={`w-full flex items-center justify-between px-4 py-2 ml-6 mr-2 rounded-lg text-sm transition-all duration-200 ${
                                    isPathActive(item?.path)
                                      ? 'bg-primary text-primary-foreground shadow-sm'
                                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                                  }`}
                                >
                                  <div className="flex items-center space-x-3">
                                    <Icon name={item?.icon} size={16} />
                                    <span>{item?.label}</span>
                                  </div>
                                  {/* Sub-item alert badge */}
                                  {itemAlertCount > 0 && (
                                    <AlertBadge 
                                      count={itemAlertCount} 
                                      severity={getHighestSeverity(itemAlerts)}
                                      size="sm"
                                    />
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>)
                    )}
                  </div>
                );
              })}
            </div>
          </nav>

          {/* Footer with current active section and alert summary */}
          {!isCollapsed && !isCompactMode && (
            <div className="p-4 border-t border-border">
              <div className="text-xs text-muted-foreground space-y-1">
                <div>Active: {menuStructure?.find(s => s?.id === expandedGroup)?.label || 'None'}</div>
                {alertCount > 0 && (
                  <div className="flex items-center space-x-2 text-xs">
                    <AlertBadge 
                      count={alertCount} 
                      severity={getHighestSeverity(alerts)} 
                      size="xs"
                    />
                    <span className="text-red-600 font-medium">
                      {alertCount} active alert{alertCount > 1 ? 's' : ''}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </aside>
      {/* Mobile Overlay */}
      {!isCollapsed && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onToggle}
        />
      )}
    </>
  );
};

export default Sidebar;